<template>
  <layout-private  :customer="customer">

      <div class="row">
        <div v-if="customer && !customer.role_count" class="text-center col-title col-12 col-lg-6 offset-lg-3 col-xl-6 offset-xl-3">
          <h4 class="light-weight snug">Getting Started: Step 4 of 5</h4>
          <h1>Create Roles</h1>
        </div> <!-- /.col-x -->
        <div v-if="customer && !customer.role_count" class="text-center col-12 col-lg-10 offset-lg-1 col-xl-8 offset-xl-2">
          <p>Next, add the team member roles your dental group uses. Roles are similar to job titles like Dentist or Hygienist, but a team member can have multiple roles assigned to them. Roles are different from access levels that control access to admin pages in Smiley, which you can set in the next step.</p>
          <p class="sm"><em>Note: <strong>roles cannot be deleted</strong> after you save them.</em></p>
          <p v-if="!customer"><i class="far fa-fw fa-pulse fa-spinner"></i></p>
        </div> <!-- /.col-x -->
        <div v-if="customer && customer.role_count" class="col-12 col-lg-6">
          <h1 class="title-short">Roles</h1>
        </div> <!-- /.col-x -->
        <div v-if="customer && customer.role_count" class="col-12 col-lg-6 text-lg-right">
          <p class="snug sm light-weight mt-2"><em>Note: roles cannot be deleted after you've saved them.</em></p>
        </div> <!-- /.col-x -->

        <div class="col-12">
          <hr />
        </div>

        <div class="col-12 col-lg-6">
          <h3>Common Roles</h3>
          <p>Here are some common roles used by many dental groups. Check the box next to each role to include it. You can also edit the name of each of these roles as needed.</p>
          <div class="row row-common-roles">
            <div v-for="(role, indexRole) in roles" v-if="role.type =='common'" class="col-role col-6">
              <div class="form-check">
                <input v-model="role.include" class="form-check-input" type="checkbox" :id="'role' + indexRole">
                <!-- <label :for="'role' + indexRole" name="roleName" class="form-control">{{ role.roleName }}</label> -->
                <input for="'role' + indexRole" :disabled="!role.include" v-model="role.roleName" name="locationName" class="form-control" placeholder="Enter Role Name" type="text" />
              </div> <!-- /.input-group -->
            </div> <!-- /.col-6 -->
          </div> <!-- /.row -->
        </div> <!-- /.col-x -->

        <div class="col-12 col-lg-6">
          <div class="box-blue">
            <div class="box-padding">
              <h3>Custom Roles</h3>
              <div class="row">
                <div v-for="(role, indexRole) in roles" v-if="role.type =='custom' || (role.id && oldRoles.some(oldRole => oldRole.id === role.id))" class="col-role col-6">
                  <div class="input-group input-group-append">
                    <input v-model="role.roleName" name="locationName" class="form-control" placeholder="Enter Role Name" type="text" />
                    <button
                      class="btn btn-remove"
                      v-if="!role.id && !oldRoles.some(oldRole => oldRole.id === role.id)"
                      @click="removeRole(indexRole)"
                    >
                      <i class="far fa-times"></i>
                    </button>
                    <button
                      class="btn btn-hide btn-icon-only btn-no-animation"
                      v-if="role.id && oldRoles.some(oldRole => oldRole.id === role.id)"
                      @click="hideRole(indexRole)"
                    >
                      <i v-if="!role.hidden && !role.savingHiddenState" class="far fa-eye fa-tooltip"><span class="tip">
                        This role is visible. Click to hide in Smiley Mobile.
                      </span></i>
                      <i v-if="role.hidden && !role.savingHiddenState" class="far fa-eye-slash fa-tooltip "><span class="tip">
                        This role is hidden. Click to show in Smiley Mobile.
                      </span></i>
                      <i v-if="role.savingHiddenState" class="far fa-fw fa-pulse fa-spinner"></i>
                    </button>

                  </div> <!-- /.input-group -->
                </div> <!-- /.col-6 -->
                <div class="col-role col-12 d-flex align-items-center">
                  <button @click.prevent="addRole()" class="btn btn-link">
                    <i class="far fa-plus-circle"></i>Add Role
                  </button>
                </div>
              </div> <!-- /.row -->
            </div> <!-- /.box-padding -->
          </div> <!-- /.box-blue -->
        </div> <!-- /.col-x -->


        <div v-if="customer" class="col-footer col-12 text-center">
          <hr />
          <list-errors :errors="errors"></list-errors>
          <p v-if="loading"><i class="far fa-fw fa-pulse fa-spinner"></i></p>
          <p v-if="customer.role_count" class="instructions"><small><em>Note: Editing the name of a role will cause every team member with that role to have the new role name.</em></small></p>
          <p v-if="roles.some(role => role.hidden !== null && role.hidden !== undefined)" class="instructions"><small><em>Note: When hiding or showing a role, changes are automatically saved.</em></small></p>
          <p v-if="success" class="instructions"><i class="far fa-fw fa-check green-color"></i> Updates Saved Successfully.</p>
          <p class="snug"><button v-if="!loading" @click.prevent="saveRoles" class="btn btn-app">
            <span v-if="!customer.role_count">Save &amp; Continue<i class="far fa-angle-right fa-fw"></i></span>
            <span v-if="customer.role_count">Save Updates</span>
          </button></p>
        </div>

      </div> <!-- /.row -->
  </layout-private>
</template>

<script>
import Vue from 'vue'
import LayoutPrivate from '../components/LayoutPrivate.vue'
import {mapGetters} from 'vuex'
import ListErrors from '../components/ListErrors.vue'

export default {
  props: ['customer'],
  components: {
    LayoutPrivate, ListErrors,
  },
  data () {
    return {
      errors: [],
      loading: false,
      success: false,
      roles: [
        {roleName: "Dentist", type: "common", include: false},
        {roleName: "Hygienist", type: "common", include: false},
        {roleName: "Dental Assistant", type: "common", include: false},
        {roleName: "Office Manager", type: "common", include: false},
        {roleName: "Billing Coordinator", type: "common", include: false},
        {roleName: "Patient Coordinator", type: "common", include: false},
      ],
      oldRoles: [],
    }
  },
  computed: {
    ...mapGetters(['user', 'user_props', 'custom_claims']),
  },
  watch: {
    customer() {
      // don't load if roles already exist to be saved
        // could have just saved them and be waiting on redirect.
        // cust object updates when new roles are saved
      if(this.customer && !this.roles.some(role => role.include)) {
        this.loadRoles();
      }
    },
    // check for redirect on loading custom claims
    custom_claims() {
      if(!this.custom_claims || !this.custom_claims.adminLevel || this.custom_claims.adminLevel !== "superAdmin") {
        this.$router.replace('/')
      }
    },
  },
  methods: {
    addRole() {
      this.roles.push({roleName: null, type: "custom", include: true});
    },
    removeRole(index) {
      if(this.roles[index]) {
        this.roles.splice(index,1);
      }
    },
    hideRole(index) {
      if(this.roles[index]) {
        let role = this.roles[index];
        let hiddenStatus;
        // if hidden, unhide it
        if(role.hidden) {
          hiddenStatus = false;
        }
        // if not hidden, hide it
        if(!role.hidden) {
          hiddenStatus = true;
        }
        // save updates
        this.$set(this.roles, index, {
          ...role,
          savingHiddenState: true,
          hidden: hiddenStatus,
        });
        // this.roles[index].savingHiddenState = true;
        // this.roles[index].hidden = hiddenStatus;
        Vue.firestore.doc(`customers/${this.customer.id}/roles/${role.id}`)
          .update({
            hidden: hiddenStatus,
          })
          .then(() => {
            this.$set(this.roles, index, {
              ...role,
              savingHiddenState: false,
              hidden: hiddenStatus,
            });
          })
          .catch(err => console.log(err));
      }
    },
    async loadRoles() {
      if(this.customer && !this.oldRoles.length) {
        try {
          this.loading = true;
          let rolesQuerySnap = await Vue.firestore.collection('customers')
            .doc(this.customer.id)
            .collection('roles')
            .get();
          // console.log({ rolesQuerySnap });
          if(rolesQuerySnap.empty) {
            console.log("couldn't find roles in your account.")
            // if none found, exit
            this.loading = false;
            return true;
          }
          for(const role of rolesQuerySnap.docs) {
            // console.log(group.data())
            let roleData = {
              ...role.data(),
              id: role.id,
              type: "custom",
              include: true,
            }
            // build completely separate objects to compare changes against
              // must be new objects or they will be reactive and comparisons
              // will fail
            let oldRoleData = {
              ...role.data(),
              id: role.id,
              type: "custom",
              include: true,
            }
            // console.log({roleData});
            this.roles.push(roleData);
            this.oldRoles.push(oldRoleData);
            this.loading = false;
          }
        } catch(err) {
          this.errors.push(err.message)
          console.log(err);
        }
      }
    },
    async saveRoles() {
      // reset errors
      this.errors = [];
      this.loading = true;
      // validate logged in
      if(!this.user) {
        this.errors.push('You must be logged in. Please refresh the page.')
      }
      // validate all roles have names
      if(this.roles.some(role => role.include && !role.roleName)) {
        this.errors.push("All roles must have a name. Please remove any role fields you don't need.")
      }
      // validate if names are only spaces
      if(this.roles.some( role => role.include && role.roleName && !role.roleName.trim().length)) {
        this.errors.push("Role names cannot be only spaces.")
      }
      // validate role name length
      if(this.roles.some( role => role.include && role.roleName && role.roleName.length < 2)) {
        this.errors.push("Role names must be at least 2 characters long.")
      }
      // validate some included roles
      if(!this.roles.some(role => role.include)) {
        this.errors.push("You must have some roles to save.")
      }
      // validate no roles have the same names
      const roleNames = new Set(this.roles.map(role => {
        if(role.include) {
          return role.roleName;
        }
      }));
      // delete undefined values, caused by Array.map not returning a value
        // for each role that has role.include == false
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/map#mapped_array_contains_undefined
      roleNames.delete(undefined);
      // console.log(roleNames)
      if(roleNames.size < this.roles.filter(role => role.include).length) {
        this.errors.push("Oops, you included multiple roles with the same name, or didn't make any changes.")
      }
      // stop if errors
      if(this.errors.length) {
        this.loading = false;
        return;
      }

      var batch = Vue.firestore.batch();
      // save a count of roles to continue setup
      let custRef = Vue.firestore
        .collection('customers').doc(this.customer.id);
      // console.log("role_count",this.roles.filter(role => role.include).length)
      let roleCount = this.roles.filter(role => role.include).length;
      if(!this.customer.role_count || this.customer.role_count < roleCount) {
        batch.update(custRef, {
          role_count: roleCount,
        });
      }

      this.roles.forEach( async (role) => {
        if(role.include) {
          let ref;
          // if a new role
          if(!this.oldRoles.length || !this.oldRoles.some(oldRole => oldRole.id == role.id)) {
            ref = Vue.firestore.collection('customers')
              .doc(this.customer.id)
              .collection('roles')
              .doc(); // sets auto-generated id since batch.add() is not valid
            batch.set(ref, {
              roleName: role.roleName,
            });
          }
          // if an old role
          else if(this.oldRoles.length && this.oldRoles.some(oldRole => oldRole.id == role.id && oldRole.roleName !== role.roleName)) {
            ref = Vue.firestore.collection('customers')
              .doc(this.customer.id)
              .collection('roles')
              .doc(role.id); // sets auto-generated id since batch.add() is not valid
            batch.update(ref, {
              roleName: role.roleName,
            });
          }
        }
      })
      // console.log('roles saved', this.oldRoles.length)
      // commit all
      batch.commit()
      .then(() => {
        // console.log('roles saved', this.oldRoles.length)
        if(this.oldRoles.length) {
          // reset data and reload
          this.oldRoles = [];
          this.roles = [
            {roleName: "Dentist", type: "common", include: false},
            {roleName: "Hygienist", type: "common", include: false},
            {roleName: "Dental Assistant", type: "common", include: false},
            {roleName: "Office Manager", type: "common", include: false},
            {roleName: "Billing Coordinator", type: "common", include: false},
            {roleName: "Patient Coordinator", type: "common", include: false},
          ];
          this.loadRoles();
          this.success = true;
        } else {
          this.$router.push('/team')
        }
        this.loading = false;
      })
      .catch((err) => {
        this.errors.push(`An error occured: ${err.message}`);
        console.log(err);
      });
    },
  },
  mounted () {
    // this.checkCustomer(); // if check here will always redirect away on page refresh
    this.loadRoles();
  },
}
</script>

<style lang="less" scoped>
@import "../assets/less/variables.less";

.box-blue {
  background-color: fadeout(@color-blue, 50%);
  height: 100%;
}
.box-padding {
  padding: 30px;
}
.row-common-roles {
  margin: 0; // rm negative margins

  .col-role:nth-of-type(odd) {
    padding-left: 0;
  }
  .col-role:nth-of-type(even) {
    padding-right: 0;
  }
}
.col-role {
  margin-bottom: 30px;
}
.input-group,
.input-group .form-control,
.form-check .form-control {
  margin: 0;
}
.form-check .form-check-input {
  margin-top: 17.5px;
}
.col-footer {
  padding-bottom: 50px;
}
.btn-hide {
  // override tip width
  .fa-tooltip .tip {
    margin-left: -125px;
    width: 250px;
  }
  // prevent other btns from being on top of tip
  &:hover {
    z-index: 1500;
  }
}
p.instructions + p.instructions {
  margin-top: -15px;
}

</style>
